import * as React from 'react';
import { useReactiveVar } from '@apollo/client';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import { useParams } from 'react-router';
import { useMutation } from '@apollo/client';
import { graphql } from '../../../__generated__';
import Block from '../../../components/common/Block';
import { displayMoney } from 'gmd-utilities-package';
import { Box } from '@mui/material';
import { currentRequest, newRequestState, refreshRequestsVar } from '../common';
import {
  DestinationCurrency,
  SendCurrency,
  SendMode,
} from '../../../__generated__/graphql';
import useCommonNavigations from '../../../hooks/Navigation';

/*type DuplicateRequest = {
  uid: string;
};

type DuplicateWarningProps = {
  duplicates: DuplicateRequest[];
};

const DuplicateWarning = (props: DuplicateWarningProps) => {
  const { duplicates = [] } = props;
  if (duplicates && duplicates.length > 0) {
    return (
      <Alert severity="warning">
        <AlertTitle>Possible Duplicate Submission</AlertTitle>
        This recepient has received the same amount within a short period.
        Please confirm to make sure this will not result in multiple payments.
        Submissions to the customer are listed below:
        {duplicates.map((item) => {
          return (
            <div key={item.uid}>
              — <strong>{item.uid}</strong>
            </div>
          );
        })}
      </Alert>
    );
  } else {
    return null;
  }
};*/

const addTransactionMutation = graphql(/* GraphQL */ `
  mutation AddTransaction(
    $senderId: String!
    $senderName: String!
    $sendingPhone: String!
    $recipientId: String!
    $recipientName: String!
    $destinationPhone: String!
    $sendAmount: Float!
    $sendCurrency: SendCurrency!
    $receiveAmount: Float!
    $receiveCurrency: DestinationCurrency!
    $exchangeRate: Float!
    $sendMode: SendMode!
    $isBonus: Boolean!
    $purpose: String!
    $billNumber: String!
    $billAmount: Float!
    $billDate: String!
    $billDueDate: String!
    $billCustomerNumber: String!
    $billMerchant: String!
    $billPayItemDesc: String!
    $billPayItemId: String!
    $billServiceId: String!
    $billServiceName: String!
    $billServiceNumber: String!
  ) {
    addTransaction(
      senderId: $senderId
      senderName: $senderName
      sendingPhone: $sendingPhone
      recipientId: $recipientId
      recipientName: $recipientName
      destinationPhone: $destinationPhone
      sendAmount: $sendAmount
      sendCurrency: $sendCurrency
      receiveAmount: $receiveAmount
      receiveCurrency: $receiveCurrency
      exchangeRate: $exchangeRate
      sendMode: $sendMode
      isBonus: $isBonus
      purpose: $purpose
      billNumber: $billNumber
      billAmount: $billAmount
      billDate: $billDate
      billDueDate: $billDueDate
      billCustomerNumber: $billCustomerNumber
      billMerchant: $billMerchant
      billPayItemDesc: $billPayItemDesc
      billPayItemId: $billPayItemId
      billServiceId: $billServiceId
      billServiceName: $billServiceName
      billServiceNumber: $billServiceNumber
    ) {
      ...TransactionResponseFragment
    }
  }
`);

const updateTransactionMutation = graphql(/* GraphQL */ `
  mutation UpdateTransaction(
    $id: ID!
    $senderId: String!
    $senderName: String!
    $sendingPhone: String!
    $recipientId: String!
    $recipientName: String!
    $destinationPhone: String!
    $sendAmount: Float!
    $sendCurrency: SendCurrency!
    $receiveAmount: Float!
    $receiveCurrency: DestinationCurrency!
    $exchangeRate: Float!
    $sendMode: SendMode!
    $isBonus: Boolean!
    $purpose: String!
    $billNumber: String!
    $billAmount: Float!
    $billDate: String!
    $billDueDate: String!
    $billCustomerNumber: String!
    $billMerchant: String!
    $billPayItemDesc: String!
    $billPayItemId: String!
    $billServiceId: String!
    $billServiceName: String!
    $billServiceNumber: String!
  ) {
    updateTransaction(
      id: $id
      senderId: $senderId
      senderName: $senderName
      sendingPhone: $sendingPhone
      recipientId: $recipientId
      recipientName: $recipientName
      destinationPhone: $destinationPhone
      sendAmount: $sendAmount
      sendCurrency: $sendCurrency
      receiveAmount: $receiveAmount
      receiveCurrency: $receiveCurrency
      exchangeRate: $exchangeRate
      sendMode: $sendMode
      isBonus: $isBonus
      purpose: $purpose
      billNumber: $billNumber
      billAmount: $billAmount
      billDate: $billDate
      billDueDate: $billDueDate
      billCustomerNumber: $billCustomerNumber
      billMerchant: $billMerchant
      billPayItemDesc: $billPayItemDesc
      billPayItemId: $billPayItemId
      billServiceId: $billServiceId
      billServiceName: $billServiceName
      billServiceNumber: $billServiceNumber
    ) {
      ...TransactionResponseFragment
    }
  }
`);

const ReviewPage = () => {
  const transaction = useReactiveVar(currentRequest);
  const { backToBookmark } = useCommonNavigations();
  const { id } = useParams();
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const [addTransaction, { loading: addLoading }] = useMutation(
    addTransactionMutation,
    {
      variables: {
        senderId: transaction?.sender.id || '',
        senderName: transaction?.sender.name || '',
        sendingPhone: transaction?.sender?.phone || '',
        recipientId: transaction?.recipient.id || '',
        recipientName: transaction?.recipient.name || '',
        destinationPhone: transaction?.recipient?.phone || '',
        sendAmount: transaction?.sent.amount || 0,
        sendCurrency: (transaction?.sent.currency || 'usd') as SendCurrency,
        receiveAmount: transaction?.received.amount || 0,
        receiveCurrency: (transaction?.received.currency ||
          'xaf') as DestinationCurrency,
        exchangeRate: transaction?.exchangeRate || 0,
        sendMode: (transaction?.sendMode || 'SEND') as SendMode,
        isBonus: transaction?.isBonus || false,
        purpose: transaction?.purpose || '',
        billNumber: transaction?.bill?.billNumber || '',
        billAmount: transaction?.bill?.amount || 0,
        billDate: transaction?.bill?.date || '',
        billDueDate: transaction?.bill?.dueDate || '',
        billCustomerNumber: transaction?.bill?.customerNumber || '',
        billMerchant: transaction?.bill?.merchant || '',
        billPayItemDesc: transaction?.bill?.payItemDesc || '',
        billPayItemId: transaction?.bill?.payItemId || '',
        billServiceId: transaction?.bill?.serviceId || '',
        billServiceName: transaction?.bill?.serviceName || '',
        billServiceNumber: transaction?.bill?.serviceNumber || '',
      },
    }
  );
  const [updateTransaction, { loading: updateLoading }] = useMutation(
    updateTransactionMutation,
    {
      variables: {
        id: id || '',
        senderId: transaction?.sender.id || '',
        senderName: transaction?.sender.name || '',
        sendingPhone: transaction?.sender?.phone || '',
        recipientId: transaction?.recipient.id || '',
        recipientName: transaction?.recipient.name || '',
        destinationPhone: transaction?.recipient?.phone || '',
        sendAmount: transaction?.sent.amount || 0,
        sendCurrency: (transaction?.sent.currency || 'usd') as SendCurrency,
        receiveAmount: transaction?.received.amount || 0,
        receiveCurrency: (transaction?.received.currency ||
          'xaf') as DestinationCurrency,
        exchangeRate: transaction?.exchangeRate || 0,
        sendMode: (transaction?.sendMode || 'SEND') as SendMode,
        isBonus: transaction?.isBonus || false,
        purpose: transaction?.purpose || '',
        billNumber: transaction?.bill?.billNumber || '',
        billAmount: transaction?.bill?.amount || 0,
        billDate: transaction?.bill?.date || '',
        billDueDate: transaction?.bill?.dueDate || '',
        billCustomerNumber: transaction?.bill?.customerNumber || '',
        billMerchant: transaction?.bill?.merchant || '',
        billPayItemDesc: transaction?.bill?.payItemDesc || '',
        billPayItemId: transaction?.bill?.payItemId || '',
        billServiceId: transaction?.bill?.serviceId || '',
        billServiceName: transaction?.bill?.serviceName || '',
        billServiceNumber: transaction?.bill?.serviceNumber || '',
      },
    }
  );
  if (transaction === null) return null;
  const {
    isBonus,
    sender,
    recipient,
    sent,
    received,
    exchangeRate,
    purpose,
    bill,
  } = transaction;
  const isBillPay = Boolean(bill?.merchant && bill?.serviceName);
  const headerTitle = isBillPay ? 'Billpay' : 'Order';
  const onBack = () => newRequestState('input');
  const onSubmit = async () => {
    if (id) {
      await updateTransaction();
    } else {
      await addTransaction();
    }
    refreshRequestsVar(true);
    currentRequest(null);
    backToBookmark('REQUESTS');
  };

  const loading = addLoading || updateLoading;

  return (
    <React.Fragment>
      {loading && <LinearProgress />}
      <Typography variant="h6" sx={{ textAlign: 'center' }}>
        {`${headerTitle} Summary${isBonus ? ' - Customer Bonus' : ''}`}
      </Typography>
      <Grid
        container
        spacing={1}
        justifyContent="space-between"
        alignItems="center"
      >
        <Grid item xs={12} sm={6}>
          <Block title="Sender">
            <Grid container>
              <Grid item xs={6}>
                <Typography variant="subtitle1" align="left">
                  {sender.name}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" align="right">
                  Tel: {sender.phone}
                </Typography>
              </Grid>
            </Grid>
          </Block>
        </Grid>
        {isBillPay && (
          <Grid item xs={12} sm={6}>
            <Block title="Recipient">
              <Grid container>
                <Grid item xs={6}>
                  <Typography variant="subtitle1" align="left">
                    {bill?.merchant}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" align="right">
                    Acc#: {bill?.serviceNumber}
                  </Typography>
                </Grid>
              </Grid>
            </Block>
          </Grid>
        )}
        {!isBillPay && (
          <Grid item xs={12} sm={6}>
            <Block title="Recipient">
              <Grid container>
                <Grid item xs={6}>
                  <Typography variant="subtitle1" align="left">
                    {recipient.name}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" align="right">
                    Tel: {recipient.phone}
                  </Typography>
                </Grid>
              </Grid>
            </Block>
          </Grid>
        )}
        <Grid item xs={12}>
          <Block title="Amount">
            <Grid container>
              <Grid item xs={6}>
                <Typography variant="subtitle1" align="left">
                  Send
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography
                  variant="subtitle2"
                  align="right"
                  sx={{ fontWeight: '700' }}
                >
                  {displayMoney(sent.amount, sent.currency)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle1" align="left">
                  Receive
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography
                  variant="subtitle2"
                  align="right"
                  sx={{ fontWeight: '700' }}
                >
                  {displayMoney(received.amount, received.currency)}
                </Typography>
              </Grid>
            </Grid>
          </Block>
        </Grid>
        <Grid item xs={12}>
          <Block title="Exchange Rate">
            <Grid container>
              <Grid item xs={6}>
                <Typography variant="subtitle1" align="left">
                  {displayMoney(1, sent.currency)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography
                  variant="subtitle2"
                  align="right"
                  sx={{ fontWeight: '700' }}
                >
                  {displayMoney(exchangeRate, received.currency)}
                </Typography>
              </Grid>
            </Grid>
          </Block>
        </Grid>
        {!isBillPay && (
          <Grid item xs={12}>
            <Block title="Purpose">
              <Grid container>
                <Grid item>
                  <Typography variant="subtitle1" align="left">
                    {purpose}
                  </Typography>
                </Grid>
              </Grid>
            </Block>
          </Grid>
        )}
      </Grid>
      {/* <DuplicateWarning duplicates={duplicates} /> */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button onClick={onBack} sx={{ marginTop: 3, marginLeft: 1 }}>
          Back
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={onSubmit}
          sx={{ marginTop: 3, marginLeft: 1 }}
          disabled={loading}
        >
          {!isBonus ? 'Request' : 'Request Bonus'}
        </Button>
      </Box>
    </React.Fragment>
  );
};

export default ReviewPage;
